import { useCallback, useRef, useState } from "react";

import { BrebQrService, classifyResolutionError } from "../services/brebQr.service";
import type { BrebQrResolution, BrebQrResolutionError } from "../types/brebQr.types";
import { isUnsupportedBrebQr } from "../utils/brebQrPayment";

export type BrebQrResolutionState =
  | { status: "idle" }
  | { status: "loading"; qr: string }
  | { status: "resolved"; qr: string; data: BrebQrResolution }
  | { status: "failed"; qr: string; error: BrebQrResolutionError; message: string | null };

export function useBrebQrResolution() {
  const [state, setState] = useState<BrebQrResolutionState>({ status: "idle" });
  const requestId = useRef(0);

  const resolve = useCallback(async (qr: string) => {
    const id = ++requestId.current;
    setState({ status: "loading", qr });
    try {
      const data = await BrebQrService.resolve(qr);
      if (id !== requestId.current) return;
      setState(
        isUnsupportedBrebQr(data)
          ? { status: "failed", qr, error: "unsupported", message: null }
          : { status: "resolved", qr, data },
      );
    } catch (error) {
      if (id !== requestId.current) return;
      const { kind, message } = classifyResolutionError(error);
      setState({ status: "failed", qr, error: kind, message });
    }
  }, []);

  const reset = useCallback(() => {
    requestId.current++;
    setState({ status: "idle" });
  }, []);

  return { state, resolve, reset };
}
